import { useEffect, useRef } from 'react'

import { setOptimizelyExperiment, setOptimizelyFeatureFlag } from 'actions/OptimizelyActions'
import { optimizelyIsReady, userContext } from 'analytics/optimizely/optimizely'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import createSelector from 'lib/web/createSelector'
import { OptimizelyDecideOption } from '@optimizely/optimizely-sdk'
import { reportClientError } from 'services/errorReportingService'
import config from 'constants/config'

/**
 * This is specifically for a CRO AB test, do not add anything else here.
 * Ticket to clean up: https://aussiecommerce.atlassian.net/browse/CRO-3601
 *
 * Activates the specific hero finder AB test based on the offer id.
 * Should not be used for any other AB tests.
 * @param offerId
 */
export function activateHeroFinderAbTest(offerId: string) {
  const enumKey = isOfferInHeroFinderExperiment(offerId)
  if (enumKey) activateOptimizelyExperiment(OptimizelyServerSideExperiments[enumKey])
}

export function isOfferInHeroFinderExperiment(offerId: string) {
  return Object.keys(OptimizelyServerSideExperiments).find((key) => {
    return OptimizelyServerSideExperiments[key] === `cro_hero_offer_image_${offerId}`
  })
}

export function getHeroFinderExperimentKey(offerId: string) {
  return Object.values(OptimizelyServerSideExperiments).find((value) => {
    return value === `cro_hero_offer_image_${offerId}`
  })
}

export type OptimizelyExperiments = OptimizelyClientSideExperiments | OptimizelyServerSideExperiments;

export enum OptimizelyClientSideExperiments {
  searchPriceMapMarkers = 'search_price_map_markers',
  searchTypeaheadOpenSearch = 'search_typeahead_opensearch',
  paymentsMerchantFees = 'payments_merchant_fees',
  paymentsFlightsMerchantFees = 'payments_flight_merchant_fees',
  tripExplore = 'trip_explore',
  paymentsBookingProtectionInternational = 'payments_booking_protection',
  lereTrendingDestinations = 'lere_trending_destinations',
  searchBedbankAnytimePrice = 'search_bedbank_anytime_price_days',
  offerPageHotelAndFlightsToggle = 'cro_offer_page_hotel_and_flights_toggle',
  searchMapSplitView = 'search_map_split_view',
  searchBroadSearchLocation = 'search_broad_search_location',
  steppedCheckout = 'cro_stepped_checkout',
  searchFlightDeal = 'search_flight_deal',
  cruiseSearchNewAlgorithm = 'cruise_search_new_algorithm',
  searchToursTypeahead = 'search_tours_typeahead',
  searchMobileSlimUI = 'search_mobile_slim_ui',
  cruiseNewBookingFlow = 'cruise_new_cruisebank_booking_flow_may_2024',
  aaCheckoutTest2105 = 'aa_checkout_test_21_05',
  flightPreFillRecentSearch = 'flights_pre_fill_recent_search',
  offerPageNumberOfNights = 'cro_offer_page_number_of_nights_mobile_only',
  offerShareModal = 'cro_offer_share_modal',
  searchLowMpvHidingAnywhere = 'search_low_mpv_hiding_anywhere',
  searchLowMpvHidingBali = 'search_low_mpv_hiding_bali',
}

/**
 * There are only two reasons you would use this instead of OptimizelyClientSideExperiments:
 * - Your experiment is above the fold and you want to avoid flicker
 * - You need to gain access to the variation value of a user before the experiment is activated for the
 *   report in Optimizely (you will still need to call useOptimizelyExperiment to activate the experiment
 *   despite already having the variation value stored in Redux)
 */
export enum OptimizelyServerSideExperiments {
  isSupportAssistantEnabled = 'opex_support_assistant_customer_pool',
  paymentsPayToRelease = 'payments_payto_release',
  searchFamilySegmentation = 'search_family_segmentation',
  heroFinderOfferMontigo = 'cro_hero_offer_image_0062y00000DNXXIAA5',
  heroFinderOfferSofitel = 'cro_hero_offer_image_0062y00000Id48mAAB',
  heroFinderOfferKappa = 'cro_hero_offer_image_0062y00000GwnFLAAZ',
  heroFinderOfferSebel = 'cro_hero_offer_image_0062y00000G4nkxAAB',
  heroFinderOfferKimpton = 'cro_hero_offer_image_0062y00000IdHHVAA3',
  heroFinderOfferSindhorn = 'cro_hero_offer_image_0062y00000IbErzAAF',
  heroFinderOfferVibe = 'cro_hero_offer_image_0062y00000GvFxWAAV',
  heroFinderOfferSamabe = 'cro_hero_offer_image_0062y00000GxSjqAAF',
  heroFinderOfferOvolo = 'cro_hero_offer_image_0062y00000IdSmqAAF',
  heroFinderOfferRaffles = 'cro_hero_offer_image_0062y00000GdvcwAAB',
  heroFinderOfferIntercontinental = 'cro_hero_offer_image_0062y00000Ic68KAAR',
  paymentsHotelsDeposits = 'payments_hotels_deposits',
  lpcChangeDatesEnabled = 'opex_lpc_change_dates',
  nonFlashNextAlgo = 'search_non_flash_next_algo',
  pricePerNight = 'cro_price_per_night',
  lereSearchZeroResultNearby = 'lere_zero_search_result_with_nearby_offers',
  lereSearchZeroResultNoNearby = 'lere_zero_search_result_without_nearby_offers',
  lereTourRecommendation = 'lere_tour_recommendation',
  lereGraphRecommendation = 'lere_graph_recommendation',
  mobileHomepageHeroHeadline = 'cro_mobile_homepage_headline_in_hero',
  signUpInCheckout = 'cro_sign_up_modal_in_checkout',
  bedbankChangeDatesEnabled = 'opex_bedbank_change_dates',
}

export enum OptimizelyFeatureFlags {
  luxPlusFeatureToggle = 'luxplus_feature_toggle',
  luxPlusToursToggle = 'luxplus_tours_toggle',
  isGoCardlessEnabled = 'payments_go_cardless',
  allowPhoneNumberLogin = 'allow_phone_number_login',
  promoAppBanner = 'app_banner_targeted_delivery',
  CSAgentsAutoQuote = 'cro_cs_agents_auto_quote',
  tourOptionalExtras = 'tours_optional_extras'
}

export async function activateOptimizelyExperiment(flagKey: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments | OptimizelyFeatureFlags) {
  // This function shouldn't be called on server
  if (typeof window === 'undefined') {
    return
  }

  const success = await optimizelyIsReady

  if (success) {
    try {
      const decision = userContext.decideForKeys([flagKey])[flagKey]
      const experimentValues = {
        variationKey: decision.variationKey,
        variables: decision.variables,
        enabled: decision.enabled,
      }

      return experimentValues
    } catch (e) {
      reportClientError(e, { flagKey }) }
  }
}

/**
 * Optimizely Experiments Selectors.  Each experiment can be found in Redux in state.optimizely.optimizelyExperiments.
 * Each has an 'enabled' value that can be used to determine if the user is enrolled in a variant (if the user is
 * part of the control, or the test isn't live in Optimizely, the value will be false).  Each experiment also has
 * a 'variationKey' value that can be used to determine which variant the user is enrolled in (think of this as
 * the same thing as the number variant previously used in Optimize).  Each experiment also has a 'variables'value
 * that can be used to pass variables from Optimizely to use in the experiment.
 *
 * Return the optimizely experiment variant dynamically based on the given trigger boolean condition.
 * If canTriggerExperiment is false the experiment will not be activated.
 * Note: once the trigger becomes true once, it will be enabled for the rest of the session until page refresh.
 *
 * @param canTriggerExperiment a boolean indicating whether the experiment can be triggered
 * @param experimentId experiment id from the Optimizely UI (Rule Key)
 * @returns Experiment variant if can be triggered
 */
export function useOptimizelyExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments | OptimizelyFeatureFlags, canTriggerExperiment: boolean = true) {
  const dispatch = useAppDispatch()
  const experimentActivated = useRef(false)

  useEffect(() => {
    if (canTriggerExperiment && !experimentActivated.current) {
      if (isFeatureFlagExperiment(experimentId)) {
        dispatch(setOptimizelyFeatureFlag(experimentId))
      } else {
        dispatch(setOptimizelyExperiment(experimentId))
      }
      experimentActivated.current = true
    }
  }, [dispatch, canTriggerExperiment, experimentId])

  const experimentVariation = useAppSelector(state => {
    if (isFeatureFlagExperiment(experimentId)) {
      return getOptimizelyFeatureFlagVariation(state, experimentId)
    } else {
      return getOptimizelyExperimentVariation(state, experimentId)
    }
  })

  if (canTriggerExperiment) {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) {
      return config[`OPTIMIZELY_${experimentId}`]
    }

    return experimentVariation
  }
}

export function isFeatureFlagExperiment(experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments | OptimizelyFeatureFlags): experimentId is OptimizelyFeatureFlags {
  return typeof experimentId === 'string' && Object.values(OptimizelyFeatureFlags).includes(experimentId as OptimizelyFeatureFlags)
}

/**
 *
 *  Gets all Optimizely experiments and returns an object required for Snowplow experiment context
 */
export function allOptimizelyExperiments() {
  const allExperiments = userContext.decideAll([OptimizelyDecideOption.DISABLE_DECISION_EVENT])
  return {
    flags: Object.entries(allExperiments).map(
      ([_, decisions]) => ({
        key: `exp_${decisions.flagKey}`,
        value: decisions.variationKey || '',
      }),
    ),
  }
}

export const getOptimizelyExperimentVariation = createSelector(
  (_: App.State, experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) => experimentId,
  (state: App.State, experimentId: OptimizelyClientSideExperiments | OptimizelyServerSideExperiments) => state.optimizely.optimizelyExperiments[experimentId],
  (experimentId, experiment) => {
    // For devs to set a specific variation locally. Set REACT_APP_OPTIMIZELY_[experimentId]=true for variant or false for control in .env,
    if (config[`OPTIMIZELY_${experimentId}`] !== undefined) { return config[`OPTIMIZELY_${experimentId}`] }
    if (!experiment?.enabled) {
      return
    }
    if (experiment.enabled && Object.keys(experiment.variables).length) {
      return experiment.variables[experiment.variationKey]
    }
    return experiment.variationKey
  },
)

export const getOptimizelyFeatureFlagVariation = createSelector(
  (state: App.State, experimentId: OptimizelyFeatureFlags) => state.optimizely.optimizelyFeatureFlags[experimentId],
  (experiment) => {
    return !!experiment
  },
)
